// Client for /api/tripo/avatar (functions/api/tripo/avatar.ts): start a paid
// Tripo avatar build and poll it to the finished wardrobe row. The server holds
// the Tripo key, charges the build, persists the GLB and writes the avatar row,
// so a closed tab never loses a paid avatar — it is resumable by build id.
import { gathrxrAuthHeader } from '../utils/quotaHeaders';
import type { Avatar } from './avatarService';

export interface BuildProgress {
  onStage?: (label: string) => void;
  onProgress?: (pct: number) => void;
}

export class AvatarBuildError extends Error {
  constructor(message: string, public needsPlan = false, public refunded = false) { super(message); }
}

const ENDPOINT = '/api/tripo/avatar';
const PENDING_KEY = 'gx-avatar-build';
const POLL_MS = 2500;
// A build can take up to three rig attempts (avatar.ts retry ladder: a re-rig and
// a Meshy regeneration on top of the first pass), so allow well past the ~6 min norm.
const TIMEOUT_MS = 40 * 60 * 1000;
/** How long a reload still offers to resume a remembered build. */
const PENDING_MS = 50 * 60 * 1000;

interface PendingBuild { id: string; at: number; name: string; image?: string }

export function pendingAvatarBuild(): PendingBuild | null {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null') as PendingBuild | null;
    return p && Date.now() - p.at < PENDING_MS ? p : null;
  } catch { return null; }
}
/** Forget the remembered build, but only if it is this one. */
function forgetPending(id: string) {
  if (pendingAvatarBuild()?.id === id) rememberPending(null);
}
function rememberPending(p: PendingBuild | null) {
  try { p ? localStorage.setItem(PENDING_KEY, JSON.stringify(p)) : localStorage.removeItem(PENDING_KEY); } catch { /* private mode */ }
}
export const clearPendingAvatarBuild = () => rememberPending(null);

async function call(body: Record<string, unknown>): Promise<any> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await gathrxrAuthHeader()) },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AvatarBuildError(
      data?.error?.message || data?.error || data?.message || `Avatar build failed (${res.status})`,
      !!data?.needsPlan,
      !!data?.refunded,
    );
  }
  return data;
}

/** Start a build. Returns the build id (already charged). */
export async function startAvatarBuild(params: {
  image: string;
  name: string;
  creationParams?: Record<string, string>;
  /** Small preview picture kept only so a reload can show what's building. */
  previewImage?: string;
  /** false: don't offer this build to the avatar creator's resume prompt (NPC builds). */
  remember?: boolean;
}): Promise<string> {
  const r = await call({ action: 'start', image: params.image, name: params.name, creationParams: params.creationParams || {} });
  if (params.remember === false) return r.id as string;
  const preview = params.previewImage && params.previewImage.length < 200_000 ? params.previewImage : undefined;
  rememberPending({ id: r.id, at: Date.now(), name: params.name, image: preview });
  return r.id as string;
}

/** Poll a build until it is done; resolves with the saved (inactive) wardrobe row. */
export async function waitForAvatarBuild(id: string, progress?: BuildProgress): Promise<Avatar> {
  const started = Date.now();
  let lastStage = '';
  let misses = 0;
  for (;;) {
    let r: any;
    try {
      r = await call({ action: 'poll', id });
      misses = 0;
    } catch (err) {
      if (err instanceof AvatarBuildError && /not found|Bad build/.test(err.message)) { forgetPending(id); throw err; }
      // Network blips are normal on phones; the build carries on server-side.
      if (++misses > 8) throw err;
      await new Promise((res) => setTimeout(res, POLL_MS * 2));
      continue;
    }
    if (r.stage && r.stage !== lastStage) { lastStage = r.stage; progress?.onStage?.(r.stage); }
    if (typeof r.progress === 'number') progress?.onProgress?.(r.progress);
    if (r.status === 'done' && r.avatar) { forgetPending(id); return r.avatar as Avatar; }
    if (r.status === 'failed') {
      forgetPending(id);
      throw new AvatarBuildError(r.error || 'The build failed. Nothing was charged for a failed build — try again.', false, !!r.refunded);
    }
    if (Date.now() - started > TIMEOUT_MS) {
      throw new AvatarBuildError('This is taking longer than usual. Your avatar will appear in your wardrobe when it finishes.');
    }
    await new Promise((res) => setTimeout(res, POLL_MS));
  }
}
